import React from 'react'
import { Link } from 'react-router-dom'
import { Mail, MapPin, Github, Linkedin } from 'lucide-react'
import { NAVIGATION_ITEMS, SITE_CONFIG } from '../constants'
import { cn } from '../utils'

// X / Twitter logo (lucide doesn't ship a current X mark)
const XIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    className={className}
  >
    <path d="M18.244 2H21.5l-7.51 8.583L23 22h-6.812l-5.34-6.985L4.74 22H1.482l8.04-9.187L1 2h6.969l4.83 6.385L18.244 2Zm-1.193 18h1.89L7.04 4H5.011l12.04 16Z" />
  </svg>
)

const SOCIAL_LINKS = [
  { label: 'GitHub',   href: '#', Icon: Github },
  { label: 'LinkedIn', href: '#', Icon: Linkedin },
  { label: 'X',        href: '#', Icon: XIcon },
] as const

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear()

  return (
    <footer className="relative z-10 bg-gray-50 dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main footer content */}
        <div className="py-12 md:py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
            {/* Brand */}
            <div className="lg:col-span-2">
              <Link
                to="/"
                className="inline-block text-xl font-bold text-gray-900 dark:text-white mb-4"
                aria-label={`${SITE_CONFIG.name} homepage`}
              >
                {SITE_CONFIG.name}
              </Link>
              <p className="text-gray-600 dark:text-gray-400 mb-4 max-w-sm leading-relaxed">
                An Africa-focused think-and-do tank helping institutions turn data,
                AI, and digital transformation into inclusive development impact.
              </p>
              <p className="text-sm font-medium tracking-wider uppercase text-primary-600 dark:text-primary-300">
                From Insight to Impact
              </p>
            </div>

            {/* Navigation */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider mb-4">
                Navigation
              </h3>
              <nav aria-label="Footer navigation">
                <ul className="space-y-3">
                  {NAVIGATION_ITEMS.map((item) => (
                    <li key={item.path}>
                      <Link
                        to={item.path}
                        className={cn(
                          'text-gray-600 dark:text-gray-400',
                          'hover:text-gray-900 dark:hover:text-white',
                          'transition-colors duration-200',
                          'focus:outline-none focus-visible:text-primary-500'
                        )}
                      >
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>

            {/* Contact */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider mb-4">
                Contact
              </h3>
              <ul className="space-y-3">
                <li>
                  <a
                    href="mailto:info@matno.org"
                    className={cn(
                      'flex items-center gap-2',
                      'text-gray-600 dark:text-gray-400',
                      'hover:text-gray-900 dark:hover:text-white',
                      'transition-colors duration-200',
                      'group'
                    )}
                  >
                    <Mail className="w-4 h-4 flex-shrink-0" />
                    <span>info@matno.org</span>
                  </a>
                </li>
                <li>
                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                    <MapPin className="w-4 h-4 flex-shrink-0" />
                    <span>Africa</span>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-gray-200 dark:border-gray-800 py-6">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-500 dark:text-gray-500">
              &copy; {currentYear} {SITE_CONFIG.name}. All rights reserved.
            </p>
            <div className="flex items-center gap-3">
              {SOCIAL_LINKS.map(({ label, href, Icon }) => (
                <a
                  key={label}
                  href={href}
                  aria-label={label}
                  className={cn(
                    'w-9 h-9 rounded-full flex items-center justify-center',
                    'text-gray-500 dark:text-gray-400',
                    'border border-gray-200 dark:border-gray-800',
                    'hover:text-primary-500 dark:hover:text-primary-300',
                    'hover:border-primary-300 dark:hover:border-primary-700',
                    'transition-colors duration-200'
                  )}
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
